import type { ComponentPropsWithRef, ComponentType, ElementType, ReactNode, Ref } from "react";

import {
  Button as NewButton,
  type ButtonProps as NewButtonProps,
} from "@/newComponents/Button/Button";
import { cn } from "@/shadcn/lib/utils";

type ButtonSize = "small" | "small-label" | "medium" | "large";
export type LegacyButtonStyle = "primary" | "outline" | "text" | "contained";

type IconComponent = ComponentType<{ className?: string }>;

type ButtonOwnProps = {
  /** If `true` & `type` is not explicitly specified, button will have `type="submit"`. */
  submit?: boolean;
  /** If `true`, maps to the `destructive` variant. */
  critical?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  isHighlighted?: boolean;
  size?: ButtonSize;
  style?: LegacyButtonStyle;
  className?: string;
  StartIcon?: IconComponent;
  EndIcon?: IconComponent;
  children?: ReactNode;
};

export type LegacyButtonProps<T extends ElementType = "button"> = ButtonOwnProps & {
  component?: T;
} & Omit<ComponentPropsWithRef<T>, keyof ButtonOwnProps | "component">;

const VARIANT_MAP: Record<
  LegacyButtonStyle,
  { normal: NewButtonProps["variant"]; critical: NewButtonProps["variant"] }
> = {
  primary: { normal: "primary", critical: "destructive" },
  outline: { normal: "secondary", critical: "destructive" },
  text: { normal: "ghost", critical: "ghost" },
  contained: { normal: "primary", critical: "destructive" },
};

const SIZE_MAP: Record<ButtonSize, NewButtonProps["size"]> = {
  "small": "sm",
  "small-label": "sm",
  "medium": "md",
  "large": "lg",
};

/**
 * @deprecated Use `@/newComponents/Button` directly. Promoted from `atoms/Button` (admin-frontend-components rule 11) so promoted legacy components keep its API.
 * This wrapper translates the legacy atoms API to newComponents/Button.
 */
function LegacyButtonInner<T extends ElementType = "button">({
  submit,
  critical,
  disabled,
  fullWidth,
  isHighlighted,
  size = "medium",
  style = "primary",
  className,
  StartIcon,
  EndIcon,
  component: _component,
  children,
  ref,
  ...rest
}: LegacyButtonProps<T> & { ref?: Ref<Element> }) {
  const variant = VARIANT_MAP[style][critical ? "critical" : "normal"];
  const mappedSize = SIZE_MAP[size];

  const submitType = submit ? "submit" : "button";
  const type = "type" in rest ? (rest as Record<string, unknown>).type : submitType;

  return (
    <NewButton
      {...(rest as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      className={cn(isHighlighted && "shadow-highlight-error", className)}
      disabled={disabled}
      EndIcon={EndIcon}
      fullWidth={fullWidth}
      ref={ref as React.Ref<HTMLButtonElement>}
      size={mappedSize}
      StartIcon={StartIcon}
      type={type as React.ButtonHTMLAttributes<HTMLButtonElement>["type"]}
      variant={variant}
    >
      {children}
    </NewButton>
  );
}

export const LegacyButton = LegacyButtonInner as <T extends ElementType = "button">(
  props: LegacyButtonProps<T> & { ref?: Ref<Element> },
) => ReactNode;
